import { NgModule, inject, provideAppInitializer } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { RouterStateSnapshot } from '@angular/router';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { InitService } from './shared/shared/init.service';
import { Constant } from './shared/shared/constants';
import { IPublicClientApplication, PublicClientApplication, InteractionType, BrowserCacheLocation, LogLevel } from '@azure/msal-browser';
import { MsalGuard, MsalInterceptor, MsalBroadcastService, MsalInterceptorConfiguration, MsalModule, MsalService, MSAL_GUARD_CONFIG, MSAL_INSTANCE, MSAL_INTERCEPTOR_CONFIG, MsalGuardConfiguration, MsalRedirectComponent } from '@azure/msal-angular';
import { HTTP_INTERCEPTORS, provideHttpClient, HttpClientModule } from '@angular/common/http';
import { NgZoneInterceptor } from './shared/shared/loader/ngzone.interceptor';
import { SharedModule } from './shared/shared/shared.module';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

import { AdvisorsTreeFilterModule } from './shared/shared/advisors-tree-filter/advisors-tree-filter.module';
import { LoaderInterceptor } from './shared/shared/loader/loaderInterceptor';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatDialogModule } from '@angular/material/dialog';
import { MatListModule } from '@angular/material/list';
import { MatCardModule } from '@angular/material/card';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import { UnauthorizedComponent } from './shared/shared/unauthorized/unauthorized.component';


export function loggerCallback(logLevel: any, message: string) {
  if (logLevel === LogLevel.Error) {
    console.error('[msal]', message);
  } else if (logLevel === LogLevel.Warning) {
    console.warn('[msal]', message);
  }
}

export const _protectedResourceMap: [string, string[]][] = [[Constant.config.apiBaseUrl, Constant.config.scopes]];

export function MSALInstanceFactory(): IPublicClientApplication {
  if (!Constant.config.clientId) {
    throw new Error('Configuration must be loaded before MSAL initialization');
  }

  const msalConfig = {
    auth: {
      clientId: Constant.config.clientId,
      redirectUri: window.location.origin,
      postLogoutRedirectUri: window.location.origin,
      authority: Constant.config.instance + Constant.config.tenant,
      knownAuthorities: [Constant.config.instance],
      navigateToLoginRequestUrl: false,
      allowRedirectInIframe: false
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage
    },
    system: {
      allowPlatformBroker: false,
      loggerOptions: {
        loggerCallback,
        logLevel: LogLevel.Error,
        piiLoggingEnabled: false
      }
    }
  };

  return new PublicClientApplication(msalConfig);
}



export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  let protectedResourceMap = new Map<string, Array<string>>();

  // Use the raw base URLs with trailing slash — MSAL v6 with strictMatching:false
  // performs prefix matching, so any sub-path is automatically covered.
  if (Constant.config.apiBaseUrl) {
    protectedResourceMap.set(Constant.config.apiBaseUrl, Constant.config.scopes);
  }
  if (Constant.config.apiEnvUrl) {
    protectedResourceMap.set(Constant.config.apiEnvUrl, Constant.config.scopes);
  }

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
    strictMatching: false
  };
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: (_authService, _state: RouterStateSnapshot) => ({
      scopes: Constant.config.scopes
    }),
    loginFailedRoute: '/'
  };
}

@NgModule({
  declarations: [
    AppComponent,
    UnauthorizedComponent
  ],
  imports: [
    BrowserAnimationsModule,
    BrowserModule,
    AdvisorsTreeFilterModule,
    AppRoutingModule,
    SharedModule,
    HttpClientModule,
    MatDialogModule,
    MatListModule,
    MatCardModule,
    MsalModule, 
    MatButtonToggleModule,
    FormsModule,
    ReactiveFormsModule
  ],
  providers: [ 
  provideAppInitializer(() => {
    const initsvc = inject(InitService);
    if (window.location.href.indexOf('4200') > 0) {
      return initsvc.getConfigLocal();
    }
    return initsvc.getConfig();
  }),
  { provide: HTTP_INTERCEPTORS, useClass: NgZoneInterceptor, multi: true },
  { provide: HTTP_INTERCEPTORS, useClass: MsalInterceptor, multi: true },
  { provide: HTTP_INTERCEPTORS, useClass: LoaderInterceptor, multi: true },
  { provide: MSAL_INSTANCE,useFactory: MSALInstanceFactory},
  { provide: MSAL_GUARD_CONFIG,useFactory: MSALGuardConfigFactory},
  { provide: MSAL_INTERCEPTOR_CONFIG,useFactory: MSALInterceptorConfigFactory},


  MsalService,
  MsalGuard,
  MsalBroadcastService
],
  bootstrap: [AppComponent, MsalRedirectComponent]
})
export class AppModule { }
